// @flow
import React from 'react';

import { Formik, FormikBag, Form, Field } from 'formik';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';

import ChatBubble from '@material-ui/icons/ChatBubbleOutline';
import CheckIcon from '@material-ui/icons/Done';
import InputAdornment from '@material-ui/core/InputAdornment';
import LockIcon from '@material-ui/icons/LockOutlined';
import MailIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';

import FormikSelect from 'components/Formik/Select';
import FormikTextField from 'components/Formik/TextField';

import { mkMfaResponse, mkMfaAnswer } from 'data/institutionLogins/types';
import type { MfaResponse, MfaChallenge, MfaChallengeItem, InstitutionLogin } from 'data/institutionLogins/types';

import styles from './styles';

type Props = {
  mfaChallenge: MfaChallenge,

  onCancel: () => void,
  onSubmit: (MfaResponse, () => void, () => void) => void,
  isSubmitting: boolean,

  submitButtonLabel: string,
  cancelButtonLabel: string,

  institutionLogin: InstitutionLogin,

  classes: Object,
}

type State = {
  key: ?string,
  value: ?string,
}

class MfaChallengeForm extends React.PureComponent<Props, State> {

  form = null;
  formik: FormikBag = null;

  constructor(props) {
    super(props);

    const challenger = this.props.mfaChallenge.challenges;
    let value = null;
    let key = null;
    if (challenger) {
      const firstChallenge = challenger.first();
      if (firstChallenge && firstChallenge.answerChoices) {
        value = firstChallenge.answerChoices.first().displayText;
        key = firstChallenge.key;
      }
    }
    this.state = {
      value,
      key,
    };
  }

  componentDidMount() {
    if (this.state.key && this.state.value) {
      this.formik.setFieldValue(this.state.key, this.state.value);
    }
  }

  handleCancel = () => {
    const { onCancel } = this.props;

    if (onCancel) {
      onCancel({
        step: 'mfa',
      });
    }
  };

  handleGridChange = (event: Object) => {
    this.setState({ value: event.target.value });
    this.formik.setFieldValue(event.target.name, event.target.value);
  };

  handleSubmit = (values, _actions, mfaChallenge, onSubmit) => {
    const mfaResponse = mkMfaResponse({
      institutionLoginId: mfaChallenge.institutionLoginId,
      answers: mfaChallenge.challenges.map((challenge) => (
        mkMfaAnswer({
          id: challenge.key,
          value: values[challenge.key],
        })
      )),
      submitUrl: mfaChallenge.submitUrl,
    });
    if (onSubmit) {
      onSubmit(
        mfaResponse,
        () => { this.form && this.form.setSubmitting(false); },
        () => { this.form && this.form.setSubmitting(false); }
      );
    }
  };

  generateLine = (displayText) => {
    const { classes } = this.props;
    const text = /text/i.test(displayText);
    const email = /email/i.test(displayText);
    const call = /call/i.test(displayText);
    const number = /\bXXX-XXX-(\d*)/i.exec(displayText);
    const mailDomain = /([a-zA-Z0-9*._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi.exec(displayText);
    if (text) {
      return (
        <div className={this.props.classes.messageLine}>
          <ChatBubble/>
          {number ?
            <>
              <Typography variant="body2" className={classes.dropdownSpace}>
                Text
              </Typography>
              <Typography variant="body2" className={classes.grayText}>
                {number ? number[0] : ''}
              </Typography>
            </>
            :
            <Typography className={classes.dropdownSpace}>
              {displayText}
            </Typography>
          }
        </div>
      );
    }
    if (call) {
      return (
        <div className={classes.messageLine}>
          <PhoneIcon/>
          {number ?
            <>
              <Typography variant="body2" className={classes.dropdownSpace}>
                Call
              </Typography>
              <Typography variant="body2" className={classes.grayText}>
                {number ? number[0] : ''}
              </Typography>
            </>
            :
            <Typography className={classes.dropdownSpace}>
              {displayText}
            </Typography>
          }
        </div>
      );
    }
    if (email) {
      return (
        <div className={classes.messageLine}>
          <MailIcon/>
          {mailDomain ?
            <>
              <Typography variant="body2" className={classes.dropdownSpace}>
                Email
              </Typography>
              <Typography variant="body2" className={classes.grayText}>
                {mailDomain ? mailDomain[0] : ''}
              </Typography>
            </>
            :
            <Typography className={classes.dropdownSpace}>
              {displayText}
            </Typography>
          }
        </div>
      );
    }
    return (displayText);
  };

  renderAnswerField = (key) => (
    <Field
      component={FormikTextField}
      fullWidth
      variant="outlined"
      name={key}
      placeholder="Answer"
      style={{ paddingRight: '20px' }}
      InputProps={{
        autoFocus: true,
        endAdornment: (
          <InputAdornment position="end">
            <LockIcon style={{ color: '#706e6b' }}/>
          </InputAdornment>
        ),
      }}
    />
  );

  renderChallenge = (challenge: MfaChallengeItem) => {
    const { classes, mfaChallenge } = this.props;
    const options = challenge.answerChoices && (mfaChallenge.challenges.size === 1);
    return (
      <div className={classes.challenge} key={challenge.key}>
        {challenge.base64Image &&
          <div className={classes.imageContainer}>
            <img alt="mfa" src={`data:image/jpeg;base64, ${challenge.base64Image}`} />
          </div>
        }
        <Typography variant="body2" className={options ? classes.bigQuestion : classes.littleQuestion}>
          {challenge.question}
        </Typography>
        {challenge.answerChoices ? this.renderAnswerChoices(challenge.key, challenge.answerChoices) : this.renderAnswerField(challenge.key)}
      </div>
    );
  };

  renderAnswerChoices = (key, choices) => {
    const { classes } = this.props;
    return choices.size > 3 ?
      <Field
        component={FormikSelect}
        fullWidth
        variant="outlined"
        value={this.state.value}
        name={key}
        input={
          <OutlinedInput
            id="key"
            labelWidth={0}
          />
        }
        MenuProps={{
          classes: {
            paper: classes.menu,
          },
        }}
      >
        {choices.map((choice) => (
          <MenuItem key={choice.displayText} value={choice.displayText}>
            {this.generateLine(choice.displayText)}
          </MenuItem>
        ))}
      </Field>
      :
      <Field
        name={key}
        value={this.state.value}
        render={({ field }) => (
          <RadioGroup
            {...field}
            aria-label={key}
            name={key}
            value={this.state.value}
            onChange={this.handleGridChange}
            className={classes.answerGrid}
          >
            {choices.map((choice) => (
              <FormControlLabel
                key={choice.displayText}
                value={choice.displayText}
                control={<Radio/>}
                label={
                  <div className={classes.iconLine}>
                    {this.generateLine(choice.displayText)}
                    {this.state.value === choice.displayText && <CheckIcon className={classes.checkIcon}/>}
                  </div>
                }
                className={this.state.value === choice.displayText ? classes.shadedLabel : classes.formLabel}
              />
            ))}
          </RadioGroup>
        )}
      />;
  };

  render() {
    const {
      cancelButtonLabel,
      classes,
      mfaChallenge,
      onSubmit,
      submitButtonLabel,
    } = this.props;

    if (!mfaChallenge) {
      return null;
    }

    return (
      <Formik
        onSubmit={(values, actions) => this.handleSubmit(values, actions, mfaChallenge, onSubmit)}
        ref={(form) => { this.form = form; }}
        render={(formikBag) => {
          const { handleSubmit: formikHandleSubmit, isSubmitting } = formikBag;
          this.formik = formikBag;
          return (
            <Form onSubmit={formikHandleSubmit} className={classes.form}>
              <div className={classes.formContent}>
                <div className={classes.challenges}>
                  {mfaChallenge.challenges.map((challenge) => this.renderChallenge(challenge))}
                </div>
              </div>
              <div className={classes.formActions}>
                <Button
                  color="primary"
                  className={classes.cancelButton}
                  onClick={this.handleCancel}
                >
                  {cancelButtonLabel}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  className={classes.fatButton}
                >
                  {submitButtonLabel}
                </Button>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

MfaChallengeForm.defaultProps = {
  submitButtonLabel: 'Submit',
  cancelButtonLabel: 'Cancel',
};



export default withStyles(styles, { name: 'MfaChallengeForm' })(MfaChallengeForm);
